import { type TFunction } from 'i18next';
import { z } from 'zod';
import {
  MATERIAL_VALUES,
  LIGHTING_VALUES,
  parseCoordinateInput,
  mediaTypeRequiresDimensions,
  type MediaType,
} from '@ooh/contracts';

// eslint-disable-next-line max-lines-per-function
export function createFormSchema(
  t: TFunction<'inventory'>,
  canViewSupplier = true,
  mediaType: MediaType = 'billboard',
) {
  const nonNegative = (v: string | undefined) => !v || parseFloat(v) >= 0;
  const dimensionsRequired = mediaTypeRequiresDimensions(mediaType);
  return z
    .object({
      // A caller who lacks supplier.view cannot see or supply a valid value for
      // these fields, so they must not block saving unrelated changes — the
      // .min(1) requirement only applies when the caller can actually see the
      // field (field-visibility, CU-86cbgv42n).
      supplierNumber: canViewSupplier
        ? z.string().min(1, t('form.validation.supplierNumberRequired'))
        : z.string(),
      supplierId: canViewSupplier
        ? z
            .string()
            .min(1, t('form.validation.supplierRequired'))
            .regex(/^\d+$/, t('form.validation.mustBeValidSelection'))
        : z.string(),
      formatId: z
        .string()
        .min(1, t('form.validation.formatRequired'))
        .regex(/^\d+$/, t('form.validation.mustBeValidSelection')),
      regionCode: z.string().min(1, t('form.validation.regionRequired')),
      countyName: z.string().optional(),
      agglomeration: z.string().optional(),
      city: z.string().min(1, t('form.validation.cityRequired')),
      address: z.string().min(1, t('form.validation.addressRequired')),
      // Required-ness is enforced by the superRefine below (conditional on
      // mediaType, mirroring the import adapter rule) rather than .min(1) here,
      // so a blank value is only an error for dimension-required media types.
      widthM: z.string().refine((v: string) => !v || parseFloat(v) > 0, {
        message: t('form.validation.widthPositive'),
      }),
      heightM: z.string().refine((v: string) => !v || parseFloat(v) > 0, {
        message: t('form.validation.heightPositive'),
      }),
      material: z.enum(MATERIAL_VALUES).or(z.literal('')).optional(),
      lighting: z.enum(LIGHTING_VALUES).or(z.literal('')).optional(),
      latitude: z
        .string()
        .optional()
        .refine((v) => !v || parseCoordinateInput(v) !== null, {
          message: t('form.validation.invalidCoordinate'),
        }),
      longitude: z
        .string()
        .optional()
        .refine((v) => !v || parseCoordinateInput(v) !== null, {
          message: t('form.validation.invalidCoordinate'),
        }),
      notes: z.string().optional(),
      wfContactPerson: z.string().optional(),
      locationDescription: z.string().optional(),
      catalogPrice: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      rent: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      productionCost: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      installCost: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      dismantlingCost: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      exposureCost: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      additionalCosts: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      discountedPrice: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      lastMinutePrice: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      starterPrice: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      clientProduction: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      clientInstallation: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      clientDismantling: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      clientAdditionalFees: z
        .string()
        .optional()
        .refine(nonNegative, { message: t('form.validation.nonNegative') }),
      tagDefinitionIds: z.array(z.number().int().positive()).optional(),
    })
    .superRefine((data, ctx) => {
      if (!dimensionsRequired) return;
      if (!data.widthM) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['widthM'],
          message: t('form.validation.widthRequired'),
        });
      }
      if (!data.heightM) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['heightM'],
          message: t('form.validation.heightRequired'),
        });
      }
    });
}

export type FormValues = z.infer<ReturnType<typeof createFormSchema>>;
